import {Component, OnInit, ViewChild} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ActivatedRoute, Router} from '@angular/router';
import {UntypedFormGroup} from '@angular/forms';
import {PaginatedTableComponent} from '~shared/components/tables/paginated-table/paginated-table.component';
import {LocalModalComponent} from '~shared/components/elements/local-modal/local-modal.component';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {TablesTranslations} from '~shared/components/tables/tables-translations.service';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {SidebarService} from '~core/components/sidebar/sidebar.service';
import {
  AttributeService,
  NavigationService,
  ScreenSizeService,
  StorageService,
  TixxSelectService,
  TixxSelectSubmissionService
} from '~core/services';
import {Attribute, Club, Lottery, LotteryExecutionSummary, SidebarOptions, TixxSelectSubmission} from '~core/models';

/**
 * Component that displays the lottery execution process
 */
@UntilDestroy()
@Component({
  selector: 'app-lottery-execution',
  templateUrl: './lottery-execution.component.html',
  styleUrls: ['../../lottery-execution.component.scss']
})

export class LotteryExecutionComponent implements OnInit {
  @ViewChild(PaginatedTableComponent) paginatedTable: PaginatedTableComponent;
  @ViewChild(LocalModalComponent) resultsDataTable: LocalModalComponent;
  public savedFilteredForm: UntypedFormGroup;
  public savedDeviationForm: any;
  public stepProcess: number = 1;
  public selectedLottery: Lottery;
  public isOpened: boolean = true;
  public selectedClub: Club;

  public clubAttributes: Array<Attribute>;

  public i18nEmptyState: string;
  public columns: Array<string> = [];
  public keys: Array<Array<string>> = [];
  public baseApi: string;
  public customQueries: Array<any> = [];
  public currentSubmissions: Array<TixxSelectSubmission>;
  public executionSummary: LotteryExecutionSummary;
  public iterationSubmissionFilters: any;

  public i18nModalTitle: string;

  constructor(
    private storage: StorageService,
    private navigationService: NavigationService,
    private sidebarService: SidebarService,
    private tixxSelectService: TixxSelectService,
    protected attributeService: AttributeService,
    private submissionService: TixxSelectSubmissionService,
    public spinnerService: SpinnerService,
    public buttonsTranslations: ButtonsTranslations,
    public flTranslations: FormLabelTranslationService,
    private tablesTranslations: TablesTranslations,
    private router: Router,
    private route: ActivatedRoute,
    private screenSizeService: ScreenSizeService,
  ) {
    if (this.screenSizeService.screenSize === 'desktop') {
      this.sidebarService.setSidebar(new SidebarOptions(true, true));
    } else {
      this.sidebarService.setSidebar(new SidebarOptions(false, true));
    }
    this.sidebarService.castIsOpened.pipe(untilDestroyed(this))
      .subscribe(sidebarOptions => this.isOpened = sidebarOptions.isOpened);
    this.navigationService.setCurrentPage('lotteryExecution');
    this.navigationService.setCurrentBreadcrumbs(['crm', 'lotteryExecution']);
    this.storage.castSelectedClub.pipe(untilDestroyed(this)).subscribe((data: Club) => this.selectedClub = data);
    this.tixxSelectService.castSelectedLottery.pipe(untilDestroyed(this))
      .subscribe((lottery: Lottery) => {
        if (!lottery.isExecutionMode) {
          this.router.navigate(['../'], { relativeTo: this.route });
        } else {
          this.selectedLottery = lottery;
        }
      });
    this.submissionService.castSubmissionList.pipe(untilDestroyed(this))
      .subscribe((data: Array<TixxSelectSubmission>) => {
        if (data) {
          this.currentSubmissions = data;
        }
      });
  }

  ngOnInit(): void {
    this.i18nModalTitle = $localize`:@@tixxSelect.modalTitle.resultsDataTable:Results data table`;

    this.setUp();
    this.getExecutionSubmissions(this.selectedLottery.id);
  }

  /**
   * Adds the columns and keys for the table
   */
  protected setUp(): void {
    this.columns.push(
      this.tablesTranslations.i18nDateOfSubmission,
      this.tablesTranslations.i18nFirstName,
      this.tablesTranslations.i18nLastName,
      this.tablesTranslations.i18nStreet,
      this.tablesTranslations.i18nPostalCode,
      this.tablesTranslations.i18nPriority
    );
    this.keys.push(['submittedAt', 'customer.firstName', 'customer.lastName', 'customer.street', 'customer.postalCode', 'priority']);
  }

  /**
   * Get the submissions for the current results table.
   */
  public getExecutionSubmissions(id: number): any {
    const endpoint = this.submissionService.getExecutionSubmissionEndpoint(id);
    this.baseApi = endpoint[0];
    this.customQueries = endpoint[1];
  }

  public openResultsTable(): void {
    this.resultsDataTable.setModalStatus(true);
    this.paginatedTable.refetchData();
  }

  /**
   * Sets the new submissions list
   */
  public createSubmissionObject(reminders: Array<TixxSelectSubmission>): void {
    const data = this.submissionService.createSubmissionObject(reminders);
    this.submissionService.setSubmissionsList(data);
  }

  public goBackAStep(): void {
    this.stepProcess -= 1;
  }

  public goForwardAStep(): void {
    this.stepProcess += 1;
  }
}
